import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { 
  Phone, MapPin, Clock, CheckCircle, ChevronRight, 
  Wrench, Shield, Star, MessageCircle, Loader2,
  AlertCircle, Home
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEOServicePage = () => {
  const { citySlug, districtSlug, serviceId } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPage = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/seo/page/${citySlug}/${districtSlug}/${serviceId}`);
        setPage(response.data.page);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Sayfa bulunamadı' : 'Bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [citySlug, districtSlug, serviceId]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
        <Footer />
      </>
    );
  }

  if (error || !page) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">{error || 'Sayfa Bulunamadı'}</h1>
            <p className="text-gray-600 mb-4">Aradığınız sayfa mevcut değil veya henüz yayınlanmamış.</p>
            <Link to="/">
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Home className="w-4 h-4 mr-2" /> Ana Sayfaya Dön
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const content = page.content || {};
  const faultCodes = page.fault_codes || {};

  // Helper function to extract text from content (handles both string and object formats)
  const getTextContent = (section) => {
    if (!section) return null;
    if (typeof section === 'string') return section;
    if (typeof section === 'object' && section.content) return section.content;
    return null;
  };

  const getSectionTitle = (section, defaultTitle) => {
    if (typeof section === 'object' && section.title) return section.title;
    return defaultTitle;
  };

  // Schema.org JSON-LD
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `HizmetGeliyo - ${page.district} ${page.service_name}`,
    "description": content.meta_description,
    "url": `https://hizmetgeliyo.com${page.url_path}`,
    "telephone": "08502427808",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": page.district,
      "addressRegion": page.city,
      "addressCountry": "TR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "41.0082",
      "longitude": "28.9784"
    },
    "priceRange": "₺₺",
    "openingHours": "Mo-Su 08:00-22:00"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": content.faq_items?.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    })) || []
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Ana Sayfa", "item": "https://hizmetgeliyo.com" },
      { "@type": "ListItem", "position": 2, "name": page.city, "item": `https://hizmetgeliyo.com/${page.city_slug}` },
      { "@type": "ListItem", "position": 3, "name": page.district, "item": `https://hizmetgeliyo.com/${page.city_slug}/${page.district_slug}` },
      { "@type": "ListItem", "position": 4, "name": page.service_name, "item": `https://hizmetgeliyo.com${page.url_path}` }
    ]
  };

  return (
    <>
      <Navbar />
      <Helmet>
        <title>{content.seo_title || `${page.district} ${page.service_name} | HizmetGeliyo`}</title>
        <meta name="description" content={content.meta_description} />
        <meta name="keywords" content={content.keywords?.join(', ')} />
        <link rel="canonical" href={`https://hizmetgeliyo.com${page.url_path}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={content.seo_title} />
        <meta property="og:description" content={content.meta_description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://hizmetgeliyo.com${page.url_path}`} />
        <meta property="og:site_name" content="HizmetGeliyo" />
        
        {/* Schema.org */}
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="max-w-6xl mx-auto px-4 py-3">
            <nav className="flex items-center text-sm text-gray-600">
              <Link to="/" className="hover:text-blue-600">Ana Sayfa</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span>{page.city}</span>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span>{page.district}</span>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span className="text-blue-600 font-medium">{page.service_name}</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16">
          <div className="max-w-6xl mx-auto px-4">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {content.h1_title || `${page.district} ${page.service_name}`}
            </h1>
            <p className="text-lg text-blue-100 mb-6 max-w-2xl">
              {page.district}, {page.city} bölgesinde profesyonel {page.service_name.toLowerCase()} hizmeti
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:08502427808">
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white">
                  <Phone className="w-5 h-5 mr-2" />
                  Hemen Ara: 0850 242 78 08
                </Button>
              </a>
              <Link to="/#talep">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Online Talep Oluştur
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Content Area */}
            <div className="lg:col-span-2 space-y-8">
              {/* Intro */}
              <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {content.intro_paragraph}
                </p>
              </div>

              {/* Services Section */}
              {getTextContent(content.services_section) && (
                <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                    <Wrench className="w-6 h-6 text-blue-600 mr-3" />
                    {getSectionTitle(content.services_section, 'Sunduğumuz Hizmetler')}
                  </h2>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {getTextContent(content.services_section)}
                  </p>
                </div>
              )}

              {/* Why Us Section */}
              {getTextContent(content.why_us_section) && (
                <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                    <Shield className="w-6 h-6 text-green-600 mr-3" />
                    {getSectionTitle(content.why_us_section, 'Neden HizmetGeliyo?')}
                  </h2>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {getTextContent(content.why_us_section)}
                  </p>
                </div>
              )}

              {/* Fault Codes Section */}
              {Object.keys(faultCodes).length > 0 && (
                <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                    <AlertCircle className="w-6 h-6 text-orange-600 mr-3" />
                    Yaygın Arıza Kodları
                  </h2>
                  <div className="space-y-6">
                    {Object.entries(faultCodes).map(([brand, codes]) => (
                      <div key={brand}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">{brand}</h3>
                        <div className="grid gap-2">
                          {codes.slice(0, 5).map((code, idx) => (
                            <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                              <span className="px-2 py-1 bg-red-100 text-red-700 font-mono text-sm rounded">
                                {code.code}
                              </span>
                              <span className="text-gray-600 text-sm">{code.description}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* FAQ Section */}
              {content.faq_items?.length > 0 && (
                <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                    <MessageCircle className="w-6 h-6 text-purple-600 mr-3" />
                    Sıkça Sorulan Sorular
                  </h2>
                  <div className="space-y-4">
                    {content.faq_items.map((faq, idx) => (
                      <div key={idx} className="border-b border-gray-100 pb-4 last:border-0">
                        <h3 className="font-semibold text-gray-800 mb-2">{faq.question}</h3>
                        <p className="text-gray-600">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-4 space-y-6">
                {/* Contact Card */}
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Hemen İletişime Geçin</h3>
                  <div className="space-y-4">
                    <a 
                      href="tel:08502427808" 
                      className="flex items-center gap-3 p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors"
                    >
                      <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                        <Phone className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">7/24 Destek Hattı</p>
                        <p className="font-bold text-gray-800">0850 242 78 08</p>
                      </div>
                    </a>
                    <Link 
                      to="/#talep"
                      className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors"
                    >
                      <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                        <MessageCircle className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Online Talep</p>
                        <p className="font-bold text-gray-800">Hızlı Teklif Alın</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Service Area */}
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    <MapPin className="w-5 h-5 text-blue-600 mr-2" />
                    {getSectionTitle(content.service_area_section, 'Hizmet Bölgesi')}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {page.district}, {page.city} ve çevresinde profesyonel hizmet veriyoruz.
                  </p>
                  {getTextContent(content.service_area_section) && (
                    <p className="text-sm text-gray-500">{getTextContent(content.service_area_section)}</p>
                  )}
                </div>

                {/* Trust Badges */}
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Neden Biz?</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-gray-600">Garantili Hizmet</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-blue-500" />
                      <span className="text-gray-600">Aynı Gün Servis</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Star className="w-5 h-5 text-yellow-500" />
                      <span className="text-gray-600">Uzman Teknisyenler</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-purple-500" />
                      <span className="text-gray-600">Şeffaf Fiyatlandırma</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        {content.cta_text && (
          <div className="bg-gradient-to-r from-orange-500 to-red-500 py-12">
            <div className="max-w-4xl mx-auto px-4 text-center">
              <p className="text-2xl font-bold text-white mb-6">{content.cta_text}</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:08502427808">
                  <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100">
                    <Phone className="w-5 h-5 mr-2" />
                    0850 242 78 08
                  </Button>
                </a>
                <Link to="/#talep">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    Online Talep Oluştur
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default SEOServicePage;
